import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { downloadStoredFile } from "@/lib/storage-url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, ReceiptText } from "lucide-react";
import TemplateFieldEditor, { type PlacedField } from "./TemplateFieldEditor";
import {
  TemplateSectionCard,
  TemplateLoading,
  TemplateEmptyState,
  TemplateFieldReference,
  TemplateRowActions,
  DefaultBadge,
} from "./templates/TemplateSectionUI";
import { PdfUploadField } from "./templates/PdfUploadField";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDateNumeric } from "@/lib/date-format";

// ── Invoice field names ──────────────────────────────────────────────
// Matches the CoLiviq management-invoice template placeholders
// (Management_CoLiviq_Invoice_Template.docx). Seller/bank details are
// pre-printed on the template itself, so only the variable values are fields.
const INVOICE_FIELDS = [
  // Recipient (owner)
  "OWNER_NAME",
  "OWNER_STREET_ADDRESS",
  "OWNER_POSTAL_CITY",
  "PROPERTY_ADDRESS",
  // Invoice header
  "INVOICE_NUMBER",
  "INVOICE_DATE",
  "SERVICE_PERIOD",
  "DUE_DATE",
  // Amounts
  "MANAGEMENT_FEE",
  "NET_AMOUNT",
  "VAT_AMOUNT",
  "TOTAL_AMOUNT",
] as const;

/** Sample values used to fill the preview PDF so the admin can verify placement. */
const SAMPLE_DATA: Record<string, string> = {
  OWNER_NAME: "Hans Mustermann",
  OWNER_STREET_ADDRESS: "Musterstraße 12",
  OWNER_POSTAL_CITY: "10115 Berlin",
  PROPERTY_ADDRESS: "Hauptstraße 5, 80333 München",
  INVOICE_NUMBER: "RE-2026-0042",
  INVOICE_DATE: "13.07.2026",
  SERVICE_PERIOD: "Juli 2026",
  DUE_DATE: "23.07.2026",
  MANAGEMENT_FEE: "15 % der Nettokaltmiete",
  NET_AMOUNT: "100,84 EUR",
  VAT_AMOUNT: "19,16 EUR",
  TOTAL_AMOUNT: "120,00 EUR",
};

type InvoiceTemplate = {
  id: string;
  name: string;
  description: string | null;
  template_pdf_url: string | null;
  fill_mode: string;
  vector_overlay_config: { fields: PlacedField[] } | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
};

export function AdminInvoiceTemplates() {
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();

  // ── List state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<InvoiceTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templatePendingDelete, setTemplatePendingDelete] =
    useState<InvoiceTemplate | null>(null);

  // ── Form state ──
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    template_pdf_url: "",
    is_default: false,
  });

  // ── Preview state ──
  const [rawPdfBytes, setRawPdfBytes] = useState<Uint8Array | null>(null);
  const [placedFields, setPlacedFields] = useState<PlacedField[]>([]);

  // ── Auto-load PDF when editing an existing template ──
  useEffect(() => {
    if (!dialogOpen || rawPdfBytes || !formData.template_pdf_url) return;
    let cancelled = false;
    (async () => {
      try {
        // Through the storage API, not the stored public URL: the contracts
        // bucket is going private (COL-436).
        const blob = await downloadStoredFile(
          formData.template_pdf_url,
          "contracts"
        );
        if (cancelled) return;
        const bytes = new Uint8Array(await blob.arrayBuffer());
        if (cancelled) return;
        setRawPdfBytes(bytes);
      } catch {
        // Silently fail — user can click upload to retry
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, formData.template_pdf_url]);

  // ── Queries ──
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["invoice-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoice_templates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      // vector_overlay_config is jsonb; InvoiceTemplate asserts its shape.
      return data as unknown as InvoiceTemplate[];
    },
  });

  // ── Mutations ──
  const saveMutation = useMutation({
    mutationFn: async (payload: {
      id?: string;
      name: string;
      description: string | null;
      template_pdf_url: string;
      is_default: boolean;
      vector_overlay_config?: { fields: PlacedField[] } | null;
    }) => {
      if (payload.is_default) {
        // Clear the previous default first. If this fails, bail rather than
        // letting the insert/update below trip idx_invoice_templates_single_default
        // with a cryptic unique-violation message.
        const { error: undefaultError } = await supabase
          .from("invoice_templates")
          .update({ is_default: false })
          .eq("is_default", true);
        if (undefaultError) throw undefaultError;
      }
      if (payload.id) {
        const { error } = await supabase
          .from("invoice_templates")
          .update({
            name: payload.name,
            description: payload.description,
            fill_mode: "vector_overlay",
            template_pdf_url: payload.template_pdf_url,
            is_default: payload.is_default,
            vector_overlay_config: payload.vector_overlay_config ?? null,
            updated_at: new Date().toISOString(),
          } as any)
          .eq("id", payload.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("invoice_templates").insert({
          name: payload.name,
          description: payload.description,
          fill_mode: "vector_overlay",
          template_pdf_url: payload.template_pdf_url,
          is_default: payload.is_default,
          vector_overlay_config: payload.vector_overlay_config ?? null,
        } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-templates"] });
      toast.success(
        editingTemplate
          ? t("admin.invoiceTemplates.toastUpdated", "Template updated")
          : t("admin.invoiceTemplates.toastCreated", "Template created")
      );
      closeDialog();
    },
    onError: (err: Error) => {
      toast.error(
        t(
          "admin.invoiceTemplates.toastSaveFailed",
          "Failed to save template: {error}"
        ).replace("{error}", err.message)
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("invoice_templates")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-templates"] });
      toast.success(
        t("admin.invoiceTemplates.toastDeleted", "Template deleted")
      );
    },
    onError: (err: Error) => {
      toast.error(
        t(
          "admin.invoiceTemplates.toastDeleteFailed",
          "Failed to delete: {error}"
        ).replace("{error}", err.message)
      );
    },
  });

  // ── Helpers ──
  function resetState() {
    setRawPdfBytes(null);
    setPlacedFields([]);
  }

  function emptyForm() {
    return {
      name: "",
      description: "",
      template_pdf_url: "",
      is_default: false,
    };
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingTemplate(null);
    resetState();
    setFormData(emptyForm());
  }

  function openCreate() {
    setEditingTemplate(null);
    resetState();
    setFormData(emptyForm());
    setDialogOpen(true);
  }

  function openEdit(tpl: InvoiceTemplate) {
    setEditingTemplate(tpl);
    resetState();
    if (tpl.vector_overlay_config?.fields) {
      setPlacedFields(tpl.vector_overlay_config.fields);
    }
    setFormData({
      name: tpl.name,
      description: tpl.description ?? "",
      template_pdf_url: tpl.template_pdf_url ?? "",
      is_default: tpl.is_default,
    });
    setDialogOpen(true);
  }

  function openDeleteConfirm(tpl: InvoiceTemplate) {
    setTemplatePendingDelete(tpl);
    setDeleteDialogOpen(true);
  }

  function closeDeleteConfirm() {
    if (deleteMutation.isPending) return;
    setDeleteDialogOpen(false);
    setTemplatePendingDelete(null);
  }

  function confirmDelete() {
    if (!templatePendingDelete) return;
    deleteMutation.mutate(templatePendingDelete.id, {
      onSettled: () => closeDeleteConfirm(),
    });
  }

  // ── PDF Upload ──
  async function handleFileUpload(file: File) {
    setUploading(true);
    resetState();
    try {
      const arrayBuf = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuf);
      setRawPdfBytes(bytes);

      const fileName = `invoice-templates/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(fileName, file, {
          contentType: "application/pdf",
          upsert: false,
        });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(fileName);
      setFormData((prev) => ({ ...prev, template_pdf_url: urlData.publicUrl }));

      toast.success(
        t("admin.invoiceTemplates.toastPdfUploaded", "PDF uploaded")
      );
    } catch (err: any) {
      toast.error(
        t(
          "admin.invoiceTemplates.toastUploadFailed",
          "Upload failed: {error}"
        ).replace("{error}", err.message)
      );
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit() {
    if (!formData.name.trim()) {
      toast.error(
        t("admin.invoiceTemplates.errNameRequired", "Template name is required")
      );
      return;
    }
    if (!formData.template_pdf_url.trim()) {
      toast.error(
        t(
          "admin.invoiceTemplates.errPdfRequired",
          "Template PDF is required — upload a file or paste a URL"
        )
      );
      return;
    }
    saveMutation.mutate({
      id: editingTemplate?.id,
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      template_pdf_url: formData.template_pdf_url.trim(),
      is_default: formData.is_default,
      vector_overlay_config:
        placedFields.length > 0 ? { fields: placedFields } : null,
    });
  }

  // ── Render ──
  return (
    <>
      <TemplateSectionCard
        icon={ReceiptText}
        title={t("admin.invoiceTemplates.title", "Invoice Templates")}
        description={t(
          "admin.invoiceTemplates.description",
          "Manage PDF templates for invoices. Drag the invoice fields onto the PDF to position each value (INVOICE_NUMBER, TOTAL_AMOUNT, OWNER_NAME, etc.)."
        )}
        onAdd={openCreate}
      >
        {isLoading ? (
          <TemplateLoading />
        ) : templates.length === 0 ? (
          <TemplateEmptyState
            icon={ReceiptText}
            title={t(
              "admin.invoiceTemplates.emptyTitle",
              "No invoice templates yet. Add your first invoice template."
            )}
            hint={t(
              "admin.invoiceTemplates.emptyHint",
              "Upload a PDF and place the invoice fields where each value should appear."
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>
                    {t("admin.invoiceTemplates.colName", "Name")}
                  </TableHead>
                  <TableHead>
                    {t("admin.invoiceTemplates.colDefault", "Default")}
                  </TableHead>
                  <TableHead>
                    {t("admin.invoiceTemplates.colCreated", "Created")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("admin.invoiceTemplates.colActions", "Actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{tpl.name}</span>
                        {tpl.description && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {tpl.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{tpl.is_default && <DefaultBadge />}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateNumeric(tpl.created_at, language)}
                    </TableCell>
                    <TableCell className="text-right">
                      <TemplateRowActions
                        pdfUrl={tpl.template_pdf_url}
                        downloadName={
                          tpl.name ? `${tpl.name}.pdf` : "invoice-template.pdf"
                        }
                        onEdit={() => openEdit(tpl)}
                        onDelete={() => openDeleteConfirm(tpl)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <TemplateFieldReference
          title={t(
            "admin.invoiceTemplates.fieldNamesTitle",
            "Invoice Field Names"
          )}
          description={t(
            "admin.invoiceTemplates.fieldNamesDesc",
            "Drag these onto the PDF to define where each value is stamped:"
          )}
          fields={INVOICE_FIELDS}
        />
      </TemplateSectionCard>

      {/* ── Create / Edit Dialog ── */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent
          className={
            rawPdfBytes ? "max-w-6xl max-h-[92vh] overflow-y-auto" : "max-w-lg"
          }
        >
          <DialogHeader>
            <DialogTitle>
              {editingTemplate
                ? t(
                    "admin.invoiceTemplates.dialogEditTitle",
                    "Edit Invoice Template"
                  )
                : t(
                    "admin.invoiceTemplates.dialogNewTitle",
                    "New Invoice Template"
                  )}
            </DialogTitle>
          </DialogHeader>

          <div
            className={rawPdfBytes ? "grid grid-cols-[320px_1fr] gap-6" : ""}
          >
            {/* ── Left: Form settings ── */}
            <div className="mt-1 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="inv-tpl-name">
                  {t("admin.invoiceTemplates.fieldName", "Template Name *")}
                </Label>
                <Input
                  id="inv-tpl-name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder={t(
                    "admin.invoiceTemplates.namePlaceholder",
                    "e.g. Management fee DE, Rent invoice EN"
                  )}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inv-tpl-desc">
                  {t("admin.invoiceTemplates.fieldDescription", "Description")}
                </Label>
                <Textarea
                  id="inv-tpl-desc"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder={t(
                    "admin.invoiceTemplates.descPlaceholder",
                    "Optional notes about this template"
                  )}
                  rows={2}
                />
              </div>

              <PdfUploadField
                value={formData.template_pdf_url}
                onValueChange={(v) =>
                  setFormData({ ...formData, template_pdf_url: v })
                }
                onFile={handleFileUpload}
                uploading={uploading}
                loadingExisting={!!formData.template_pdf_url && !rawPdfBytes}
              />

              <p className="text-[11px] text-muted-foreground">
                {t(
                  "admin.invoiceTemplates.dragHint",
                  "Drag invoice fields onto the PDF preview to position them."
                )}
              </p>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="inv-tpl-default"
                  checked={formData.is_default}
                  onChange={(e) =>
                    setFormData({ ...formData, is_default: e.target.checked })
                  }
                  className="rounded"
                />
                <Label
                  htmlFor="inv-tpl-default"
                  className="text-sm font-normal"
                >
                  {t(
                    "admin.invoiceTemplates.setDefault",
                    "Set as system default"
                  )}
                </Label>
              </div>

              <div className="flex justify-end gap-2 border-t pt-2">
                <Button variant="outline" onClick={closeDialog}>
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending && (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  )}
                  {editingTemplate
                    ? t("common.update", "Update")
                    : t("common.create", "Create")}
                  {placedFields.length > 0 && (
                    <span className="ml-1 text-xs opacity-70">
                      (
                      {t(
                        "admin.invoiceTemplates.fieldsCount",
                        "{count} fields"
                      ).replace("{count}", String(placedFields.length))}
                      )
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* ── Right: Visual field placement editor ── */}
            {rawPdfBytes && (
              <div className="flex min-h-0 flex-col">
                <TemplateFieldEditor
                  pdfBytes={rawPdfBytes}
                  initialFields={placedFields}
                  onChange={setPlacedFields}
                  fieldNames={INVOICE_FIELDS}
                  sampleData={SAMPLE_DATA}
                  allowDuplicates
                  whiteout
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirmation ── */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDeleteConfirm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("admin.invoiceTemplates.deleteTitle", "Delete template?")}
            </DialogTitle>
            <DialogDescription>
              {templatePendingDelete
                ? t(
                    "admin.invoiceTemplates.deleteDescNamed",
                    'This will permanently delete "{name}".'
                  ).replace("{name}", templatePendingDelete.name)
                : t(
                    "admin.invoiceTemplates.deleteDesc",
                    "This will permanently delete this template."
                  )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={closeDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {t("admin.invoiceTemplates.cancelButton", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={!templatePendingDelete || deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              {t("admin.invoiceTemplates.deleteButton", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
